import { notifications } from '@mantine/notifications';
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { authAPI } from '../lib/auth-api';
import { tokenManager, TokenStatus } from '../lib/token-manager';
import type { RegisterInput, RequestLoginCodeInput, VerifyLoginCodeInput, UpdateProfileInput, User } from '../types';
import { UserRole } from '../types';

// Auth context type
export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  register: (data: RegisterInput) => Promise<{ message: string }>;
  requestLoginCode: (data: RequestLoginCodeInput) => Promise<{ message: string; code?: string }>;
  verifyLoginCode: (data: VerifyLoginCodeInput) => Promise<void>;
  logout: () => Promise<void>;
  updateUserProfile: (data: UpdateProfileInput) => Promise<User>;
  refreshUserData: () => Promise<void>;
  error: string | null;
}

// Create auth context
const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  register: async () => ({ message: '' }),
  requestLoginCode: async () => ({ message: '' }),
  verifyLoginCode: async () => {},
  logout: async () => {},
  updateUserProfile: async () => ({
    id: '',
    email: '',
    name: '',
    role: UserRole.PRACTITIONER,
    createdAt: '',
    updatedAt: '',
  }),
  refreshUserData: async () => {},
  error: null,
});

// Hook to use auth context
export const useAuth = () => useContext(AuthContext);

// Auth provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const queryClient = useQueryClient();
  const userQueryKey = ['currentUser'];

  // Use TanStack Query for user data
  const {
    data: user,
    isLoading: isUserLoading,
    refetch: refetchUser,
  } = useQuery<User | null>({
    queryKey: userQueryKey,
    queryFn: async () => {
      try {
        return await authAPI.getCurrentUser();
      } catch {
        return null;
      }
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    gcTime: 0,
  });

  // Initialize auth on mount - just once
  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        setIsInitializing(true);
        await tokenManager.initialize();
        if (mounted) {
          await refetchUser();
        }
      } catch {
        // Silent fail on initialization errors
      } finally {
        if (mounted) {
          setIsInitializing(false);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [refetchUser]);

  // Handle navigation on auth state changes
  useEffect(() => {
    const unsubscribe = tokenManager.subscribe((event) => {
      const { status, expired } = event;
      if (status === TokenStatus.NONE || status === TokenStatus.EXPIRED) {
        queryClient.setQueryData(userQueryKey, null);
        if (expired) {
          notifications.show({
            title: 'Session Expired',
            message: 'Your session has expired. Please log in again.',
            color: 'red',
            autoClose: 5000,
          });
        }
      }
    });
    return unsubscribe;
  }, [queryClient]);

  // Auth operations with simple error handling
  const handleAuthOperation = async <T,>(operation: () => Promise<T>, errorMessage: string): Promise<T> => {
    setError(null);
    try {
      return await operation();
    } catch (error: unknown) {
      console.error('Auth operation error:', error);
      const apiError = error as { response?: { data?: { message?: string } } };
      const message = apiError.response?.data?.message || errorMessage;
      setError(message);
      throw new Error(message);
    }
  };

  // Register user
  const register = async (data: RegisterInput): Promise<{ message: string }> => {
    return handleAuthOperation(async () => {
      const response = await authAPI.register(data);
      await refetchUser();
      return response;
    }, 'Registration failed');
  };

  // Request login code
  const requestLoginCode = async (data: RequestLoginCodeInput): Promise<{ message: string; code?: string }> => {
    return handleAuthOperation(async () => {
      return await authAPI.requestLoginCode(data);
    }, 'Failed to request login code');
  };

  // Verify login code
  const verifyLoginCode = async (data: VerifyLoginCodeInput): Promise<void> => {
    await handleAuthOperation(async () => {
      const { user } = await authAPI.verifyLoginCode(data);
      queryClient.setQueryData(userQueryKey, user);
    }, 'Invalid verification code');
  };

  // Logout user
  const logout = async (): Promise<void> => {
    try {
      await authAPI.logout();
      queryClient.setQueryData(userQueryKey, null);
    } catch {
      queryClient.setQueryData(userQueryKey, null);
    }
  };

  // Force refresh user data
  const refreshUserData = async (): Promise<void> => {
    await refetchUser();
  };

  // Update user profile
  const updateUserProfile = async (data: UpdateProfileInput): Promise<User> => {
    return handleAuthOperation(async () => {
      const userData = await authAPI.updateUserProfile(data);
      queryClient.setQueryData(userQueryKey, userData);
      return userData;
    }, 'Failed to update profile');
  };

  // Derive authentication state
  const isAuthenticated = !!user;
  const isLoading = isUserLoading || isInitializing;

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        isAuthenticated,
        register,
        requestLoginCode,
        verifyLoginCode,
        logout,
        updateUserProfile,
        refreshUserData,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
